// 一次思考讨论：组建（椅子一个个坐上人）→ 立论（并排的立场，点开看论证）→ 交锋（谁驳了谁的图 + 选中的那一段）
// → 你的判断 → 教练追问 → 定稿。一屏一个焦点；资料证据收在抽屉里。
// 服务端是 lingzhi-studio；每一步发出去就返回，进行中时每秒拉一次最新状态。

import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react'
import { Link, useParams } from 'react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { App as AntApp, Drawer, Dropdown } from 'antd'
import { ArrowUp, AtSign, FileText, RotateCcw } from 'lucide-react'
import { clashThink, finalThink, getThink, interjectThink, retryThink, thinkMeta, thinkStep, THINK_RUNNING, verdictThink, type ThinkDebater, type ThinkEvidence, type ThinkSession, type ThinkStatement } from '../../api/think'
import { IconButton, Pill } from '../../ui'
import { Working } from '../../ui/Thinking'
import { useDocPreview } from '../kb/usePreview'
import { cleanTitle } from '../kb/DocCard'
import { MicButton, useDictation } from '../voice/VoiceInput'
import '../chat/chat.css'
import './think.css'

// 蓝色留给「思考讨论」本身，视角们用别的色相；「过去的你」总是紫色
const HUES = ['hue-green', 'hue-orange', 'hue-pink', 'hue-teal']
export const hueOf = (debaters: ThinkDebater[], name: string) => {
  const d = debaters.find((x) => x.name === name)
  if (d?.past) return 'hue-purple'
  const i = debaters.filter((x) => !x.past).findIndex((x) => x.name === name)
  return HUES[(i < 0 ? 0 : i) % HUES.length]
}

/** 正文里的「（证据 n）」「【证据 1、3】」「[证据2]」变成编号角标，点开那篇资料。 */
export function CitedText({ text, evidence }: { text: string; evidence: ThinkEvidence[] }) {
  const { open } = useDocPreview()
  const parts: ReactNode[] = []
  let last = 0
  for (const m of text.matchAll(/[（(【[]?证据\s*(\d+(?:\s*[、,，和]\s*\d+)*)[）)】\]]?/g)) {
    const at = m.index ?? 0
    if (at > last) parts.push(text.slice(last, at))
    for (const n of (m[1].match(/\d+/g) ?? []).map(Number)) {
      const e = evidence.find((x) => x.n === n)
      parts.push(
        e ? (
          <button key={`${at}-${n}`} type="button" className="sk-cite" aria-label={`证据 ${n}：${cleanTitle(e.title)}`} title={cleanTitle(e.title)} onClick={() => open(e.knowledgeId, e.kbId)}>
            {n}
          </button>
        ) : (
          <span key={`${at}-${n}`}>证据 {n}</span>
        ),
      )
    }
    last = at + m[0].length
  }
  if (last < text.length) parts.push(text.slice(last))
  return <>{parts}</>
}

/** 椅子上的字：「过去的你」是「你」，其他取名字的第一个字。 */
const glyph = (d: ThinkDebater) => (d.past ? '你' : [...d.name][0] ?? '·')

const md = (iso?: string) => {
  const d = new Date(iso ?? '')
  return Number.isFinite(d.getTime()) ? `${d.getMonth() + 1}/${d.getDate()}` : ''
}

/** 还在等（有人在说、教练在看）：点阵小球 + 一行流光的字 + 秒数。 */
function Waiting({ label }: { label: string }) {
  return <Working label={label} state="connecting" />
}

// ———— 头：问题、「思考讨论」、进度 ————

function stepWord(s: ThinkSession, judging: boolean) {
  if (s.phase === 'failed') return '中断了'
  if (judging && s.phase === 'positions_ready') return '你的判断'
  switch (s.phase) {
    case 'forming':
      return '组建'
    case 'positions_ready':
      return s.clashRound ? `交锋 · 第 ${s.clashRound} 轮` : '立论'
    case 'clashing':
      return `交锋 · 第 ${s.clashRound + 1} 轮`
    case 'probing':
      return '教练在看'
    case 'awaiting_final':
      return '教练追问'
    case 'closing':
      return '存档'
    case 'closed':
      return '已存档'
  }
}

function Head({ s, judging, onEvidence }: { s: ThinkSession; judging: boolean; onEvidence: () => void }) {
  const at = judging && s.phase === 'positions_ready' ? 2 : thinkStep(s)
  const word = stepWord(s, judging)
  return (
    <header className="tb-head">
      <h1>{s.topic}</h1>
      <div className="tb-meta">
        <span className="mode-tag">思考讨论</span>
        <div className="tb-steps" role="img" aria-label={`进度：${word}`}>
          {[0, 1, 2, 3].map((i) => (
            <i key={i} data-state={i < at ? 'done' : i === at ? 'now' : 'todo'} />
          ))}
          <span aria-hidden="true">{word}</span>
        </div>
        <span className="grow" />
        {s.evidence.length > 0 && (
          <button type="button" className="tb-link" onClick={onEvidence}>
            <FileText aria-hidden="true" />
            资料 {s.evidence.length}
          </button>
        )}
      </div>
    </header>
  )
}

// ———— 椅子 ————

function Seats({ s, picked, onPick, hits, showStance }: { s: ThinkSession; picked?: string; onPick?: (name: string) => void; hits?: Map<string, number>; showStance: boolean }) {
  const forming = s.phase === 'forming' || (s.phase === 'failed' && s.failedStep === 'forming')
  const empty = forming ? Math.max(0, 3 - s.debaters.length) : 0
  const n = s.debaters.length + empty
  return (
    <div className="seat-row" style={{ ['--n' as string]: n }} role="group" aria-label="各方">
      {s.debaters.map((d) => {
        const writing = s.writing.includes(d.name)
        const hit = hits?.get(d.name) ?? 0
        const body = (
          <>
            <span className="c" data-arriving={writing}>
              {glyph(d)}
              {hit > 0 && (
                <span className="hits" aria-label={`被反驳 ${hit} 次`}>
                  {hit}
                </span>
              )}
            </span>
            <b>{d.name}</b>
            {d.past && <small className="past">来自你过去的判断</small>}
            {showStance && (
              <span className="st" title={d.stance}>
                {d.stance}
              </span>
            )}
          </>
        )
        return onPick ? (
          <button key={d.name} type="button" className={`seat ${hueOf(s.debaters, d.name)}`} aria-pressed={picked === d.name} onClick={() => onPick(d.name)}>
            {body}
          </button>
        ) : (
          <span key={d.name} className={`seat ${hueOf(s.debaters, d.name)}`}>
            {body}
          </span>
        )
      })}
      {Array.from({ length: empty }, (_, i) => (
        <span key={`empty-${i}`} className="seat" aria-hidden="true">
          <span className="c" data-empty="true" data-arriving={i === 0} />
        </span>
      ))}
    </div>
  )
}

// ———— 交锋图：谁驳了谁 ————

interface Exchange {
  k: number
  st: ThinkStatement
  from: number
  to: number
}

function ClashMap({ s, exchanges, current, onPick }: { s: ThinkSession; exchanges: Exchange[]; current: number; onPick: (k: number) => void }) {
  const id = useId().replace(/:/g, '')
  // 按实际宽度画（拉伸的 SVG 会把箭头压扁）
  const box = useRef<HTMLDivElement>(null)
  const [W, setW] = useState(800)
  useEffect(() => {
    const el = box.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(([e]) => e.contentRect.width && setW(e.contentRect.width))
    ro.observe(el)
    return () => ro.disconnect()
  }, [])
  const n = s.debaters.length
  const x = (i: number) => ((i + 0.5) / n) * W
  return (
    <div className="arrows" ref={box}>
      <svg viewBox={`0 0 ${W} 120`} aria-hidden="true">
        <defs>
          {['', 'on'].map((v) => (
            <marker key={v} id={`${id}${v}`} viewBox="0 0 10 10" refX="7" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
              <path d="M0 0L10 5L0 10z" className={v ? 'ah on' : 'ah'} />
            </marker>
          ))}
        </defs>
        {exchanges
          .filter((e) => e.to >= 0)
          .map((e) => {
            const depth = 34 + (e.k % 3) * 30
            const on = e.k === current
            return <path key={e.k} className={on ? 'on' : ''} d={`M${x(e.from)} 2 C${x(e.from)} ${depth} ${x(e.to)} ${depth} ${x(e.to)} 6`} markerEnd={`url(#${id}${on ? 'on' : ''})`} />
          })}
      </svg>
      {exchanges.map((e) => {
        const depth = 34 + (e.k % 3) * 30
        const left = e.to >= 0 ? (x(e.from) + x(e.to)) / 2 / W : x(e.from) / W
        return (
          <button
            key={e.k}
            type="button"
            className="pin"
            aria-pressed={e.k === current}
            style={{ left: `${left * 100}%`, top: e.to >= 0 ? depth * 0.75 + 2 : 30 }}
            aria-label={e.st.target ? `${e.st.debater}驳${e.st.target}` : `${e.st.debater}的回应`}
            onClick={() => onPick(e.k)}
          >
            {e.k + 1}
          </button>
        )
      })}
    </div>
  )
}

// ———— 插话的记录 ————

function Interjections({ s, round }: { s: ThinkSession; round: number }) {
  // 第 r 轮回应的是第 r-1 轮之后的插话；还没交锋的那些等下一轮
  const answered = s.interjections.filter((i) => i.atRound === round - 1)
  const pending = s.phase === 'clashing' ? [] : s.interjections.filter((i) => i.atRound === s.clashRound && round === s.clashRound)
  if (!answered.length && !pending.length) return null
  return (
    <div className="tb-notes">
      {[...answered.map((i) => ({ i, note: '这一轮在回应' })), ...pending.map((i) => ({ i, note: '下一轮交锋时回应' }))].map(({ i, note }) => (
        <p key={i.at}>
          <small>
            你说{i.target ? ` · 点名「${i.target}」` : ''} · {note}
          </small>
          {i.text}
        </p>
      ))}
    </div>
  )
}

// ———— 一次思考 ————

export function ThinkSessionPage() {
  const { thinkId = '' } = useParams()
  const qc = useQueryClient()
  const { message, modal } = AntApp.useApp()
  const { open } = useDocPreview()
  const q = useQuery({
    queryKey: ['think', thinkId],
    queryFn: () => getThink(thinkId),
    refetchInterval: (query) => (query.state.data && THINK_RUNNING.includes(query.state.data.phase) ? 1000 : false),
  })
  const meta = useQuery({ queryKey: ['think-meta'], queryFn: thinkMeta, staleTime: 10 * 60_000 })
  const [inter, setInter] = useState('')
  const [target, setTarget] = useState<string | undefined>()
  const [judging, setJudging] = useState(false)
  const [lean, setLean] = useState('')
  const [final, setFinal] = useState('')
  const [picked, setPicked] = useState<string | undefined>()
  const [round, setRound] = useState(0)
  const [current, setCurrent] = useState(0)
  const [evidenceOpen, setEvidenceOpen] = useState(false)
  const [review, setReview] = useState(false)
  const interBox = useRef<HTMLInputElement>(null)
  const leanBox = useRef<HTMLTextAreaElement>(null)
  const finalBox = useRef<HTMLTextAreaElement>(null)
  const interVoice = useDictation({ value: inter, onChange: setInter, inputRef: interBox })
  const leanVoice = useDictation({ value: lean, onChange: setLean, inputRef: leanBox })
  const finalVoice = useDictation({ value: final, onChange: setFinal, inputRef: finalBox })
  const s = q.data

  // 换了一次思考：从头看
  useEffect(() => {
    setJudging(false)
    setLean('')
    setFinal('')
    setPicked(undefined)
    setReview(false)
  }, [thinkId])
  // 新一轮交锋出来：看最新那一轮的第一段
  useEffect(() => {
    if (!s) return
    setRound(s.phase === 'clashing' ? s.clashRound + 1 : s.clashRound)
    setCurrent(0)
  }, [s?.clashRound, s?.phase === 'clashing'])
  // 教练追问回来以后，定稿框里先放上刚才的倾向
  useEffect(() => {
    if (s?.phase === 'awaiting_final' && !final) setFinal(s.verdictDraft ?? '')
  }, [s?.phase])
  useEffect(() => {
    if (s?.phase === 'closed' || s?.phase === 'awaiting_final') qc.invalidateQueries({ queryKey: ['thinks'] })
    if (s?.phase === 'closed') qc.invalidateQueries({ queryKey: ['insight-judgments'] })
  }, [s?.phase, qc])

  const act = useMutation({
    mutationFn: (fn: () => Promise<ThinkSession>) => fn(),
    onSuccess: (next) => {
      qc.setQueryData(['think', thinkId], next)
      qc.invalidateQueries({ queryKey: ['thinks'] })
    },
    onError: (e) => void message.error(e.message),
  })

  const exchanges = useMemo<Exchange[]>(() => {
    if (!s) return []
    const seat = (name?: string) => s.debaters.findIndex((d) => d.name === name)
    return s.statements
      .filter((t) => t.kind === 'rebuttal' && t.round === round)
      .map((st, k) => ({ k, st, from: seat(st.debater), to: seat(st.target) }))
      .filter((e) => e.from >= 0)
  }, [s?.statements, s?.debaters, round])

  if (q.isPending) return <div className="empty-chat" aria-busy="true" />
  if (q.isError) return <p className="empty-chat">没能打开这次思考：{q.error.message}</p>
  if (!s) return null

  const maxRounds = meta.data?.limits.maxClashRounds ?? 2
  const idle = s.phase === 'positions_ready'
  const judgeView = (judging && idle) || s.phase === 'probing' || s.phase === 'awaiting_final'
  const board = !judgeView && ['forming', 'positions_ready', 'clashing'].includes(s.phase)
  const clashView = s.clashRound > 0 || s.phase === 'clashing'
  const positions = s.statements.filter((t) => t.kind === 'position')

  const clash = () => {
    if (s.clashRound >= maxRounds)
      modal.confirm({
        title: `已经交锋 ${s.clashRound} 轮了`,
        content: '再加一轮会多调用几次模型。确定要加时吗？',
        okText: '加时一轮',
        cancelText: '不用了',
        onOk: () => act.mutateAsync(() => clashThink(s.id, true)),
      })
    else act.mutate(() => clashThink(s.id))
  }
  const sendInter = async () => {
    const t = (interVoice.active ? await interVoice.stop() : inter).trim()
    if (!t) return
    act.mutate(() => interjectThink(s.id, t, target), {
      onSuccess: () => {
        setInter('')
        setTarget(undefined)
      },
    })
  }

  const hits = new Map<string, number>()
  for (const e of exchanges) if (e.st.target) hits.set(e.st.target, (hits.get(e.st.target) ?? 0) + 1)
  const ex = exchanges.find((e) => e.k === current) ?? exchanges[0]
  const rounds = Array.from({ length: s.phase === 'clashing' ? s.clashRound + 1 : s.clashRound }, (_, i) => i + 1)
  const statementOf = (name: string) => positions.find((t) => t.debater === name)

  const boardBody = (
    <div className="stage">
      {s.phase === 'forming' && !s.debaters.length && (
        <>
          <Seats s={s} showStance={false} />
          <p className="tb-note" role="status">
            {s.evidence.length ? `找到 ${s.evidence.length} 段资料，正在请人入座` : '正在找资料'}
          </p>
        </>
      )}
      {s.debaters.length > 0 && !clashView && (
        <>
          <Seats s={s} showStance picked={picked} onPick={(name) => setPicked((p) => (p === name ? undefined : name))} />
          {s.phase === 'forming' && <p className="tb-note" role="status">{s.writing.length ? `${s.writing.join('、')}在立论` : '正在立论'}</p>}
          {picked && statementOf(picked) && (
            <article className={`exchange ${hueOf(s.debaters, picked)}`} aria-label={`${picked}的论证`}>
              <div className="who">
                <span className="hdot" />
                <b>{picked}</b>的论证
              </div>
              <p className="stance">{s.debaters.find((d) => d.name === picked)?.stance}</p>
              <p>
                <CitedText text={statementOf(picked)!.text} evidence={s.evidence} />
              </p>
            </article>
          )}
          <Interjections s={s} round={0} />
        </>
      )}
      {s.debaters.length > 0 && clashView && (
        <>
          {rounds.length > 1 && (
            <div className="tb-rounds" role="group" aria-label="第几轮">
              {rounds.map((r) => (
                <button key={r} type="button" aria-pressed={r === round} onClick={() => { setRound(r); setCurrent(0) }}>
                  第 {r} 轮
                </button>
              ))}
            </div>
          )}
          <Seats s={s} showStance={false} hits={hits} />
          {exchanges.length > 0 && <ClashMap s={s} exchanges={exchanges} current={ex?.k ?? 0} onPick={setCurrent} />}
          {s.phase === 'clashing' && round === s.clashRound + 1 && !exchanges.length && <p className="tb-note" role="status">正在交锋</p>}
          {ex && (
            <article className={`exchange ${hueOf(s.debaters, ex.st.debater)}`} aria-live="polite" aria-label={`第 ${round} 轮第 ${ex.k + 1} 段`}>
              <div className="who">
                <span className="hdot" />
                <b>{ex.st.debater}</b>
                {ex.st.target ? <> 驳 <b>{ex.st.target}</b></> : ' 回应'}
              </div>
              <p>
                <CitedText text={ex.st.text} evidence={s.evidence} />
              </p>
            </article>
          )}
          {s.phase === 'clashing' && round === s.clashRound + 1 && exchanges.length > 0 && <Waiting label="还有人在说" />}
          <Interjections s={s} round={round} />
        </>
      )}
    </div>
  )

  return (
    <div className="tb">
      <div className="tb-scroll">
        <div className="tb-col">
          <Head s={s} judging={judging} onEvidence={() => setEvidenceOpen(true)} />
        </div>

        {board && boardBody}

        {judgeView && (
          <div className="tb-col tb-judge">
            <div className="ref-chips" aria-label="各方立场">
              {s.debaters.map((d) => (
                <span key={d.name} className={`ref-chip ${hueOf(s.debaters, d.name)}`} title={d.stance}>
                  <span className="hdot" />
                  {d.name}
                </span>
              ))}
            </div>
            {s.phase === 'positions_ready' && (
              <>
                <label className="sr-only" htmlFor="think-lean">你现在怎么看</label>
                <textarea id="think-lean" ref={leanBox} className="tb-field" rows={7} value={lean} placeholder="你现在怎么看？为什么？" onChange={(e) => setLean(e.target.value)} readOnly={leanVoice.active} data-dictating={leanVoice.active} autoFocus />
                <div className="tb-acts">
                  <Pill
                    className="think-go"
                    disabled={(!lean.trim() && !leanVoice.active) || act.isPending}
                    onClick={async () => {
                      const v = (leanVoice.active ? await leanVoice.stop() : lean).trim()
                      if (v) act.mutate(() => verdictThink(s.id, v))
                    }}
                  >
                    交给教练
                  </Pill>
                  <button type="button" className="tb-text" onClick={() => setJudging(false)}>
                    回到讨论
                  </button>
                  <MicButton d={leanVoice} className="tb-mic" />
                </div>
              </>
            )}
            {s.phase === 'probing' && (
              <>
                <p className="tb-draft">{s.verdictDraft}</p>
                <Waiting label="教练在看你的想法" />
              </>
            )}
            {s.phase === 'awaiting_final' && (
              <>
                <div className="probe">
                  <small>教练</small>
                  <p>{s.probe}</p>
                </div>
                <label className="sr-only" htmlFor="think-final">你的判断</label>
                <textarea id="think-final" ref={finalBox} className="tb-field" rows={6} value={final} onChange={(e) => setFinal(e.target.value)} readOnly={finalVoice.active} data-dictating={finalVoice.active} />
                <div className="tb-acts">
                  <Pill
                    className="think-go"
                    disabled={(!final.trim() && !finalVoice.active) || act.isPending}
                    onClick={async () => {
                      const v = (finalVoice.active ? await finalVoice.stop() : final).trim()
                      if (v) act.mutate(() => finalThink(s.id, v))
                    }}
                  >
                    定稿
                  </Pill>
                  <button
                    type="button"
                    className="tb-text"
                    disabled={(!final.trim() && !finalVoice.active) || act.isPending}
                    onClick={async () => {
                      const v = (finalVoice.active ? await finalVoice.stop() : final).trim()
                      if (v) act.mutate(() => verdictThink(s.id, v))
                    }}
                  >
                    再问一次
                  </button>
                  <MicButton d={finalVoice} className="tb-mic" />
                </div>
              </>
            )}
          </div>
        )}

        {s.phase === 'closing' && (
          <div className="tb-col tb-center">
            <p className="tb-verdict">{s.verdict}</p>
            <p className="tb-note" role="status">正在存进知识库</p>
          </div>
        )}

        {s.phase === 'closed' && !review && (
          <section className="tb-col tb-center" aria-label="你的判断">
            <p className="tb-small">{md(s.updatedAt)} 定稿</p>
            <p className="tb-verdict">{s.verdict}</p>
            <div className="tb-foot">
              {s.saved?.verdict && (
                <button type="button" className="tb-text" onClick={() => open(s.saved!.verdict!.knowledgeId, s.saved!.verdict!.kbId)}>
                  判决书
                </button>
              )}
              {s.saved?.map && (
                <button type="button" className="tb-text" onClick={() => open(s.saved!.map!.knowledgeId, s.saved!.map!.kbId)}>
                  分歧地图
                </button>
              )}
              <Link className="tb-text" to={`/insights?tab=judgments&think=${s.id}`}>
                在「观点」里看
              </Link>
              {s.debaters.length > 0 && (
                <button type="button" className="tb-text" onClick={() => setReview(true)}>
                  回看讨论
                </button>
              )}
              <span className="tb-small">已存进「{meta.data?.folder ?? '思考'}」</span>
            </div>
          </section>
        )}
        {s.phase === 'closed' && review && (
          <>
            {boardBody}
            <div className="tb-col">
              <button type="button" className="tb-text" onClick={() => setReview(false)}>
                回到判断
              </button>
            </div>
          </>
        )}

        {s.phase === 'failed' && (
          <>
          {s.debaters.length > 0 && boardBody}
          <div className="tb-col">
            <div className="failed" role="alert">
              <span>{s.error ?? '这一步没做完'}</span>
              <Pill size="sm" icon={<RotateCcw />} onClick={() => act.mutate(() => retryThink(s.id))} disabled={act.isPending}>
                重试
              </Pill>
            </div>
          </div>
          </>
        )}
      </div>

      {board && s.debaters.length > 0 && s.phase !== 'forming' && (
        <div className="tb-bar">
          <div className="oneline">
            <Dropdown
              trigger={['click']}
              menu={{
                items: [{ key: '', label: '不点名，大家都回应' }, ...s.debaters.map((d) => ({ key: d.name, label: d.name }))],
                selectable: true,
                selectedKeys: [target ?? ''],
                onClick: ({ key }) => setTarget(key || undefined),
              }}
            >
              <Pill size="sm" variant={target ? 'selected' : 'ghost'} icon={<AtSign />} aria-label={target ? `点名「${target}」` : '点名一位'}>
                {target ?? ''}
              </Pill>
            </Dropdown>
            <label className="sr-only" htmlFor="think-inter">插话</label>
            <input
              id="think-inter"
              ref={interBox}
              value={inter}
              placeholder={target ? `对「${target}」说…` : '插话，或 @ 一位'}
              onChange={(e) => setInter(e.target.value)}
              readOnly={interVoice.active}
              data-dictating={interVoice.active}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                  e.preventDefault()
                  void sendInter()
                }
              }}
            />
            <MicButton d={interVoice} />
            <IconButton label="发出插话" onClick={() => void sendInter()} disabled={(!inter.trim() && !interVoice.active) || act.isPending}>
              <ArrowUp strokeWidth={2.2} />
            </IconButton>
          </div>
          {clashView ? (
            <>
              <Pill variant="ghost" onClick={clash} disabled={!idle || act.isPending}>
                {s.clashRound >= maxRounds ? '加时一轮' : '再辩一轮'}
              </Pill>
              <Pill className="think-go" onClick={() => setJudging(true)} disabled={!idle}>
                我来判断
              </Pill>
            </>
          ) : (
            <>
              <Pill variant="ghost" onClick={() => setJudging(true)} disabled={!idle}>
                我来判断
              </Pill>
              <Pill className="think-go" onClick={clash} disabled={!idle || act.isPending}>
                让他们交锋
              </Pill>
            </>
          )}
        </div>
      )}

      <Drawer open={evidenceOpen} onClose={() => setEvidenceOpen(false)} title={`资料 ${s.evidence.length} 段`} width={420} className="tb-drawer">
        <EvidenceList s={s} onOpen={(e) => open(e.knowledgeId, e.kbId)} />
      </Drawer>
    </div>
  )
}

function EvidenceList({ s, onOpen }: { s: ThinkSession; onOpen: (e: ThinkEvidence) => void }) {
  const cited = new Set(s.statements.flatMap((t) => t.cites))
  const kind = (e: ThinkEvidence) => (e.kind === 'attached' ? '你挑的' : e.kind === 'verdict' ? '你过去的判断' : cited.has(e.n) ? '被引用' : '检索到的')
  return (
    <ol className="tb-ev" aria-label="资料证据">
      {s.evidence.map((e) => (
        <li key={e.n}>
          <button type="button" onClick={() => onOpen(e)}>
            <span className="sk-cite">{e.n}</span>
            <span>
              <b>{cleanTitle(e.title)}</b>
              <small>{kind(e)}</small>
              <span className="snip">{e.text}</span>
            </span>
          </button>
        </li>
      ))}
    </ol>
  )
}
